import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Settings,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Save,
  Bell,
  Shield,
  Mail
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { format } from 'date-fns'

interface GlobalSettings {
  _id: string
  maintenanceMode: boolean
  maintenanceMessage?: string
  estimatedEndTime?: Date
  minimumVersion?: string
  latestVersion?: string
  announcement?: string
  announcementPriority: 'info' | 'warning' | 'critical'
  supportContact: string
  updatedAt: Date
  createdAt: Date
}

export default function GlobalSettingsScreen() {
  const [settings, setSettings] = useState<GlobalSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form state
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState('')
  const [estimatedEndTime, setEstimatedEndTime] = useState('')
  const [minimumVersion, setMinimumVersion] = useState('')
  const [latestVersion, setLatestVersion] = useState('')
  const [announcement, setAnnouncement] = useState('')
  const [announcementPriority, setAnnouncementPriority] = useState<'info' | 'warning' | 'critical'>('info')
  const [supportContact, setSupportContact] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getGlobalSettings()
      if (response.success) {
        const data = response.data
        setSettings(data)

        // Populate form fields
        setMaintenanceMode(data.maintenanceMode)
        setMaintenanceMessage(data.maintenanceMessage || '')
        setEstimatedEndTime(data.estimatedEndTime ? format(new Date(data.estimatedEndTime), "yyyy-MM-dd'T'HH:mm") : '')
        setMinimumVersion(data.minimumVersion || '')
        setLatestVersion(data.latestVersion || '')
        setAnnouncement(data.announcement || '')
        setAnnouncementPriority(data.announcementPriority)
        setSupportContact(data.supportContact)
      } else {
        setError(response.message || 'Failed to load global settings')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load global settings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await ApiService.updateGlobalSettings({
        maintenanceMode,
        maintenanceMessage: maintenanceMessage || undefined,
        estimatedEndTime: estimatedEndTime ? new Date(estimatedEndTime).toISOString() : undefined,
        minimumVersion: minimumVersion || undefined,
        latestVersion: latestVersion || undefined,
        announcement: announcement || undefined,
        announcementPriority,
        supportContact
      })

      if (response.success) {
        setSuccess('Global settings updated successfully')
        setSettings(response.data)
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(response.message || 'Failed to update settings')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleMaintenanceMode = async () => {
    const newMode = !maintenanceMode
    setMaintenanceMode(newMode)

    try {
      const response = await ApiService.toggleMaintenanceMode(
        newMode,
        maintenanceMessage || undefined,
        estimatedEndTime ? new Date(estimatedEndTime).toISOString() : undefined
      )

      if (response.success) {
        setSuccess(`Maintenance mode ${newMode ? 'enabled' : 'disabled'}`)
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(response.message || 'Failed to toggle maintenance mode')
        setMaintenanceMode(!newMode) // Revert on error
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle maintenance mode')
      setMaintenanceMode(!newMode) // Revert on error
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Global Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage system-wide settings for the Portal API and all connected POS systems
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Maintenance Mode */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Maintenance Mode
              </CardTitle>
              <CardDescription>
                Enable to prevent all POS systems from validating subscriptions
              </CardDescription>
            </div>
            <Switch
              checked={maintenanceMode}
              onCheckedChange={handleToggleMaintenanceMode}
            />
          </div>
        </CardHeader>
        {maintenanceMode && (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="maintenanceMessage">Maintenance Message</Label>
              <Textarea
                id="maintenanceMessage"
                placeholder="System is under maintenance. Please try again later."
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This message will be shown to POS systems during maintenance
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimatedEndTime">Estimated End Time (Optional)</Label>
              <Input
                id="estimatedEndTime"
                type="datetime-local"
                value={estimatedEndTime}
                onChange={(e) => setEstimatedEndTime(e.target.value)}
              />
            </div>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> While maintenance mode is active, all POS systems will be unable to validate their subscriptions and may stop functioning.
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>

      {/* Version Control */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Version Control
          </CardTitle>
          <CardDescription>
            Set minimum and latest version requirements for POS systems
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minimumVersion">Minimum Version</Label>
              <Input
                id="minimumVersion"
                placeholder="2.0.0"
                value={minimumVersion}
                onChange={(e) => setMinimumVersion(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                POS systems below this version will be rejected
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="latestVersion">Latest Version</Label>
              <Input
                id="latestVersion"
                placeholder="2.1.0"
                value={latestVersion}
                onChange={(e) => setLatestVersion(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                For informational purposes
              </p>
            </div>
          </div>
          <Alert>
            <AlertDescription>
              Version format: <code className="text-xs bg-muted px-1 py-0.5 rounded">major.minor.patch</code> (e.g., 2.0.0)
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Announcements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-600" />
            Announcements
          </CardTitle>
          <CardDescription>
            Send messages to all connected POS systems
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="announcement">Announcement Message</Label>
            <Textarea
              id="announcement"
              placeholder="New features available! Update to version 2.1 for enhanced reporting."
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="announcementPriority">Priority</Label>
            <Select value={announcementPriority} onValueChange={(value: string) => setAnnouncementPriority(value as 'info' | 'warning' | 'critical')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info (Blue)</SelectItem>
                <SelectItem value="warning">Warning (Amber)</SelectItem>
                <SelectItem value="critical">Critical (Red)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {announcement && (
            <div className="mt-4">
              <Label>Preview</Label>
              <Alert className={
                announcementPriority === 'critical' ? 'border-red-200 bg-red-50' :
                announcementPriority === 'warning' ? 'border-amber-200 bg-amber-50' :
                'border-blue-200 bg-blue-50'
              }>
                <AlertDescription className={
                  announcementPriority === 'critical' ? 'text-red-800' :
                  announcementPriority === 'warning' ? 'text-amber-800' :
                  'text-blue-800'
                }>
                  {announcement}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Support Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-600" />
            Support Contact
          </CardTitle>
          <CardDescription>
            Contact email shown in error messages to POS systems
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supportContact">Support Email</Label>
            <Input
              id="supportContact"
              type="email"
              placeholder="support@ordore.com"
              value={supportContact}
              onChange={(e) => setSupportContact(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={loadSettings} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Save className="mr-2 h-4 w-4" />
          Save Settings
        </Button>
      </div>

      {/* Last Updated */}
      {settings && (
        <div className="text-center text-sm text-muted-foreground">
          Last updated: {format(new Date(settings.updatedAt), 'PPpp')}
        </div>
      )}
    </div>
  )
}
